import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
// Styles
import useStyles from "assets/jss/material-react/views/italmundoStyles";
import { useTheme } from "@mui/material/styles";
// Components
import PageContainer from "../../components/PageContainer/PageContainer";
import { Grid, Box, Button, useMediaQuery } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { Redireccion } from "../../components/Modals";
// Services
import ClientService from "services/clientService";
import { useRedirect, URLs } from "../../utils/redirectStep";
import {
  PrimerNombre,
  SegundoNombre,
  PrimerApellido,
  SegundoApellido,
  Email,
  Celular,
  Telefono,
} from "./Inputs";

function DatosPersonales() {
  //Inicializaciones base
  const clientService = new ClientService();
  const { checkAndRedirect } = useRedirect();
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const itIsSecondCall = useRef(false);
  const [progress, setProgress] = useState(0);
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = React.useState(false);

  const [modal, setModal] = React.useState({
    open: false,
    title: "",
    message: "",
    content: null,
  });

  // Inicializaciones particulares
  const location = useLocation();
  const [values, setValues] = React.useState({
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    email: "",
    celular: "",
    telefono: "",
  });
  const [errors, setErrors] = React.useState({
    primerNombre: false,
    segundoNombre: false,
    primerApellido: false,
    segundoApellido: false,
    email: false,
    celular: false,
    telefono: false,
  });
  const [errorMessages, setErrorMessages] = React.useState({
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    email: "",
    celular: "",
    telefono: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    checkAndRedirect(openModalRedirect, 1, params.toString());
  }, []);

  const buildNavigateUrl = (path) => {
    const params = new URLSearchParams(searchParams);
    return `${path}?${params.toString()}`;
  };

  const openModalRedirect = () => {
    setModal({
      open: true,
      showX: false,
      content: <Redireccion />,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    if (value.trim() !== "") {
      setErrors({ ...errors, [name]: false });
      setErrorMessages({ ...errorMessages, [name]: "" });
    }
  };

  const commonMistakes = [
    // Gmail typos
    "gmial.com",
    "gamil.com",
    "gnail.com",
    "gmaill.com",
    "gmail.con",
    "gmail.co",
    "gmail.om",
    "gmail.cm",
    // Hotmail typos
    "hotmial.com",
    "hotmal.com",
    "hotmai.com",
    "hotmil.com",
    "hotnail.com",
    "hotmaill.com",
    "hotmail.co",
    "hotmail.con",
    "hotmail.cm",
    // Yahoo typos
    "yaho.com",
    "yhoo.com",
    "yaho.co",
    "yaho.cm",
    "yahho.com",
    "yaoo.com",
    // Outlook typos
    "outlok.com",
    "outllok.com",
    "outlook.co",
    "outlook.cm",
    "outllook.com",
    "otulook.com",
  ];

  const handleValidateEmail = (rawEmail) => {
    if (!rawEmail) return false;
    const email = rawEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    const domain = email.split("@")[1].toLowerCase();
    return !commonMistakes.includes(domain);
  };

  const handleValidateCelular = (celular) => {
    const celularRegex = /^09\d{7}$/;
    return celularRegex.test(celular);
  };

  const handleValidateTelefono = (telefono) => {
    const telefonoRegex = /^[24]\d{7}$/;
    return telefonoRegex.test(telefono);
  };

  const handleValidation = () => {
    let valid = true;
    let newErrors = { ...errors };
    let newErrorMessages = { ...errorMessages };

    const optionalFields = ["telefono", "segundoNombre", "segundoApellido"];
    for (const field in values) {
      if (!optionalFields.includes(field) && values[field].trim() === "") {
        newErrors[field] = true;
        newErrorMessages[field] = "Este campo es obligatorio.";
        valid = false;
      }
    }

    if (!handleValidateEmail(values.email)) {
      newErrors.email = true;
      newErrorMessages.email = "Debe ser un correo electrónico válido.";
      valid = false;
    }

    if (!handleValidateCelular(values.celular)) {
      newErrors.celular = true;
      newErrorMessages.celular = "Debe ser un celular válido (09xxxxxxx).";
      valid = false;
    }

    if (values.telefono && !handleValidateTelefono(values.telefono)) {
      newErrors.telefono = true;
      newErrorMessages.telefono = "Debe ser un teléfono válido (2/4xxxxxxx).";
      valid = false;
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return valid;
  };

  const handleContinue = async () => {
    if (!handleValidation()) {
      return;
    }
    setLoading(true);

    let progressValue = 0;
    let apiResponse = null;

    const updateProgress = (increment = 4) => {
      progressValue += increment;
      if (progressValue > 100) progressValue = 100;
      setProgress(progressValue);
      setModal(() => ({
        open: true,
        showX: false,
        content: (
          <>
            <h3 className={classes.modalTitle}>
              Estamos procesando tu información
            </h3>
            <p
              className="special-font"
              style={{
                fontWeight: 700,
                color: "#04808F",
                marginTop: "1.5rem",
                fontSize: "17px",
              }}
            >
              {progressValue}%
            </p>
            <Box
              sx={{
                width: "80%",
                borderRadius: "5px",
                padding: "2px",
                border: "1px solid #04808F",
                marginTop: "0.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  backgroundColor: "#CDE6E9",
                  borderRadius: "5px",
                }}
              >
                <Box
                  sx={{
                    width: `${progressValue}%`,
                    backgroundColor: "#04808F",
                    height: "20px",
                    borderRadius: "5px",
                  }}
                />
              </Box>
            </Box>
          </>
        ),
      }));
    };

    const handleApiResponse = (response) => {
      if (response.code === 500) {
        if (itIsSecondCall.current) {
          navigate(buildNavigateUrl(URLs.intenteLuegoSiisa));
        } else {
          itIsSecondCall.current = true;
          openModalDeEsperaParaUsuario();
          setTimeout(() => {
            handleContinue();
          }, 3000);
        }
      } else {
        const respIdLead = response.data.idLead;
        if (response.data.codeError === 1) {
          setModal({ ...modal, open: false });
          navigate(buildNavigateUrl(URLs.intenteLuego), {
            state: { idLead: respIdLead },
          });
        } else if (response.data.clientType === 1) {
          const newTransaction = {
            ...location.state.transaction,
            clientType: response.data.clientType,
            idLead: respIdLead,
            codigoPaso: 2,
          };
          navigate(buildNavigateUrl(URLs.datosLaborales), {
            state: { transaction: newTransaction },
          });
        } else if (response.data.clientType === 2) {
          navigate(buildNavigateUrl(URLs.noCalifica));
        } else if (response.data.clientType === 3) {
          const newTransaction = {
            ...location.state.transaction,
            clientType: response.data.clientType,
            idLead: respIdLead,
            codigoPaso: 2,
          };
          navigate(buildNavigateUrl(URLs.newDatosLaborales), {
            state: { transaction: newTransaction },
          });
        }
      }
      setLoading(false);
    };

    const formData = {
      primerNombre: values.primerNombre,
      segundoNombre: values.segundoNombre,
      nombres: values.primerNombre + " " + values.segundoNombre,
      primerApellido: values.primerApellido,
      segundoApellido: values.segundoApellido,
      apellidos: values.primerApellido + " " + values.segundoApellido,
      correoElectronico: values.email,
      mobile: values.celular,
      telefonoFijo: values.telefono,
    };
    const apiPromise = clientService.validateClient(
      formData,
      location.state.transaction,
    );
    const progressInterval = setInterval(async () => {
      if (apiResponse?.data?.clientType || progressValue >= 100) {
        if (progressValue < 100) {
          updateProgress(100 - progressValue);
        }
        clearInterval(progressInterval);
        setTimeout(async () => {
          try {
            const response = apiResponse || (await apiPromise);
            handleApiResponse(response);
          } catch (error) {
            console.error("Error handling API call:", error);
            navigate(buildNavigateUrl(URLs.intenteLuegoSiisa));
          }
        }, 2000);
      } else {
        updateProgress();
      }
    }, 1200);

    try {
      apiResponse = await apiPromise;
    } catch (error) {
      console.error("Error fetching API response:", error);
      navigate(buildNavigateUrl(URLs.intenteLuegoSiisa));
    }
  };

  const handleModalCloseClick = () => setModal({ ...modal, open: false });

  const openModalDeEsperaParaUsuario = () => {
    const message = `<p className={classes.modalMessage}><span style="color: #d00771!important">¡Gracias por tu paciencia!</span><br /><br />Estamos procesando tu información,<br />solo tomará un momento más.</p>`;
    setModal({
      open: true,
      showX: false,
      message: message,
    });
  };

  const Subtitulo = () => {
    return (
      <p
        className={
          mobile ? `${classes.parrafoMobileOferta}` : `${classes.parrafo}`
        }
        style={{
          marginTop: mobile ? "1.2rem" : "2rem",
          color: "#04808F",
          fontWeight: 600,
          fontSize: "16px",
          textAlign: "center",
          maxWidth: mobile ? "80%" : "auto",
        }}
      >
        {mobile ? (
          <>
            La tarjeta de crédito única y <br /> hecha a tu medida.
          </>
        ) : (
          "La tarjeta de crédito única y hecha a tu medida."
        )}
      </p>
    );
  };

  return (
    <PageContainer
      loading={loading}
      modal={modal}
      handleModalCloseClick={handleModalCloseClick}
      canonicalUrl={URLs.datosPersonales}
    >
      <ProgressBar currentStep={"PASO 2"} sx={{ marginTop: "0.5rem" }} />

      <Subtitulo />
      <Grid container spacing={mobile ? 0 : 3.5}>
        <PrimerNombre
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <SegundoNombre
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <PrimerApellido
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <SegundoApellido
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <Email
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <Celular
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
        <Telefono
          handleChange={handleChange}
          values={values}
          errors={errors}
          errorMessages={errorMessages}
        />
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: mobile ? "90%" : "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
            margin: "auto",
            marginTop: "1.5rem",
          }}
        >
          <p
            style={{
              fontSize: "12px",
              lineHeight: "1.2",
              fontWeight: "480",
              margin: 0,
              marginRight: mobile ? "1rem" : "1.5rem",
            }}
          >
            CONTINUAR
          </p>
          <Button
            variant="text"
            className={` ${classes.containedBtn} `}
            disabled={loading}
            onClick={handleContinue}
            style={{
              width: "20px",
              borderTopRightRadius: "8px",
              borderEndEndRadius: "8px",
              borderStartStartRadius: "8px",
              borderBottomLeftRadius: "8px",
            }}
          >
            <ArrowForward sx={{ fontSize: 25 }} />
          </Button>
        </Box>
      </Box>
    </PageContainer>
  );
}

export default DatosPersonales;
